import { useState, useRef, useEffect } from "react"
import {
  Sparkles,
  User,
  Loader2,
  Eye,
  Clock,
  Trash2,
  RefreshCw,
  Copy,
  MessageSquarePlus,
  ChevronDown,
  MessagesSquare,
} from "lucide-react"
import toast from "react-hot-toast"
import { getModelDisplayName } from "./ModelSelector"
import type { StyleGuideSource } from "./StyleGuideBadge"
import AuraPromptInput from "./AuraPromptInput"
import type { GenerationMode } from "./AuraPromptInput"

// Model to use after first message for QuickPatch mode
const QUICKPATCH_DEFAULT_MODEL = "gpt-5.2-2025-12-11"
import { useAuraChat } from "../hooks/useAuraChat"
import { useAuraQuickPatch } from "../hooks/useAuraQuickPatch"
import { trpc } from "../../../lib/trpc"

// ============================================================================
// TYPES
// ============================================================================

// Re-export GenerationMode from AuraPromptInput

interface PageInfo {
  id: string
  name: string
  slug: string
  html_content?: string | null
}

interface ConversationInfo {
  id: string
  title: string
  message_count: number
  is_active: boolean
  updated_at: Date | string
}

interface SavedStyleGuide {
  id: string
  name: string
}

interface AuraChatSidebarProps {
  projectId: string
  pageId: string
  conversationId: string
  currentHtml: string
  onHtmlGenerated: (html: string) => void
  onStreamingChange?: (isStreaming: boolean) => void
  pages?: PageInfo[]
  conversations?: ConversationInfo[]
  onConversationChange?: (conversationId: string) => void
  onCreateConversation?: () => void
  // Style guide props
  currentPageName?: string
  savedStyleGuides?: SavedStyleGuide[]
  styleGuideSource?: StyleGuideSource
  onStyleGuideChange?: (source: StyleGuideSource) => void
  onExtractStyleGuide?: () => void
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AuraChatSidebar = ({
  projectId,
  pageId,
  conversationId,
  currentHtml,
  onHtmlGenerated,
  onStreamingChange,
  pages = [],
  conversations = [],
  onConversationChange,
  onCreateConversation,
  currentPageName,
  savedStyleGuides = [],
  styleGuideSource,
  onStyleGuideChange,
  onExtractStyleGuide,
}: AuraChatSidebarProps) => {
  const [input, setInput] = useState("")
  const [generationMode, setGenerationMode] = useState<GenerationMode>("CREATE")
  const [showConversationsDropdown, setShowConversationsDropdown] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const conversationsDropdownRef = useRef<HTMLDivElement>(null)

  // Use streaming chat hook for CREATE/EDIT modes
  const {
    messages,
    sendMessage,
    isLoading,
    isStreaming,
    streamingExplanation,
    streamingHtml,
    error,
    lastMetrics,
    refetch: refetchMessages,
  } = useAuraChat({
    conversationId,
    onHtmlUpdate: (html) => {
      // Live update the editor as HTML streams in
      onHtmlGenerated(html)
    },
    onVersionComplete: (versionNumber, html, messageId) => {
      toast.success(`Version ${versionNumber} generated`)
    },
    onError: (error) => {
      toast.error(error)
    },
  })

  // Use quick patch hook for QUICKPATCH mode
  const {
    sendQuickPatch,
    isLoading: isQuickPatchLoading,
    isStreaming: isQuickPatchStreaming,
    streamingPatches,
    patchCount,
    error: quickPatchError,
  } = useAuraQuickPatch({
    conversationId,
    onHtmlUpdate: (html) => {
      onHtmlGenerated(html)
    },
    onPatchComplete: (versionNumber, html, messageId, metrics) => {
      toast.success(`Applied ${metrics?.patchCount || 0} patches in ${((metrics?.durationMs || 0) / 1000).toFixed(1)}s`)
      refetchMessages() // Refresh message list
    },
    onError: (error) => {
      toast.error(error)
    },
  })

  const isGenerating = isLoading || isStreaming || isQuickPatchLoading || isQuickPatchStreaming

  // Notify parent when streaming state changes
  useEffect(() => {
    onStreamingChange?.(isStreaming || isQuickPatchStreaming)
  }, [isStreaming, onStreamingChange])

  // Auto-scroll to bottom when new messages arrive or streaming
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, streamingExplanation])

  // Close conversations dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (conversationsDropdownRef.current && !conversationsDropdownRef.current.contains(event.target as Node)) {
        setShowConversationsDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Switch to QuickPatch mode after first message
  const hasSetDefaults = useRef(false)
  useEffect(() => {
    if (messages.length > 0 && !hasSetDefaults.current) {
      hasSetDefaults.current = true
      setGenerationMode("QUICKPATCH")
    }
  }, [messages.length])

  // Image upload mutation
  const uploadImageMutation = trpc.aura.uploadStyleReference.useMutation()

  // Convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const result = reader.result as string
        const base64 = result.split(",")[1]
        resolve(base64)
      }
      reader.onerror = (error) => reject(error)
    })
  }

  // Handle image upload for the shared component
  const handleUploadImage = async (file: File): Promise<string> => {
    const base64 = await fileToBase64(file)
    const uploadResult = await uploadImageMutation.mutateAsync({
      projectId,
      imageBase64: base64,
      mimeType: file.type as "image/png" | "image/jpeg" | "image/webp" | "image/gif",
      filename: file.name,
    })
    return uploadResult.url
  }

  // Handle submit from the shared prompt input component
  const handleSubmit = async (options: {
    message: string
    modelId: string
    mode: GenerationMode
    styleReference?: { url?: string; imageUrl?: string }
  }) => {
    if (isGenerating) return

    const { message, modelId, mode, styleReference } = options

    // Use QuickPatch for QUICKPATCH mode, otherwise use regular streaming
    if (mode === "QUICKPATCH") {
      if (!currentHtml) {
        toast.error("No HTML to patch. Use Create mode first.")
        return
      }
      await sendQuickPatch(message, currentHtml, modelId)
    } else {
      await sendMessage(message, currentHtml || undefined, modelId, mode === "EDIT" ? "EDIT" : "CREATE", styleReference)
    }

    // Clear the input
    setInput("")
  }

  const formatTime = (date: Date | string) => {
    const d = typeof date === "string" ? new Date(date) : date
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  }

  // Get model display name from model ID
  const getModelName = (modelId?: string | null) => {
    if (!modelId) return null
    return getModelDisplayName(modelId)
  }

  // Format duration in a human-readable way
  const formatDuration = (ms?: number | null) => {
    if (!ms) return null
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(1)}s`
  }

  // Format token count
  const formatTokens = (tokens?: number | null) => {
    if (!tokens) return null
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}k`
    return tokens.toString()
  }

  const handleSelectMessage = (message: (typeof messages)[0]) => {
    if (message.generated_html) {
      onHtmlGenerated(message.generated_html)
      toast.success("Loaded version into editor")
    }
  }

  // Handle conversation switch
  const handleConversationSwitch = (convId: string) => {
    if (convId !== conversationId && onConversationChange) {
      onConversationChange(convId)
      setShowConversationsDropdown(false)
    }
  }

  // Get current conversation
  const currentConversation = conversations.find((c) => c.id === conversationId)

  // Delete message mutation
  const utils = trpc.useUtils()
  const deleteMessageMutation = trpc.aura.deleteMessage.useMutation({
    onSuccess: () => {
      toast.success("Message deleted")
      void utils.aura.listMessages.invalidate({ conversationId })
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete message")
    },
  })

  const handleDeleteMessage = (messageId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (window.confirm("Delete this message?")) {
      deleteMessageMutation.mutate({ messageId })
    }
  }

  const handleCopyMessage = (content: string, e: React.MouseEvent) => {
    e.stopPropagation()
    void navigator.clipboard.writeText(content)
    toast.success("Copied to clipboard")
  }

  const handleRegenerateMessage = (message: (typeof messages)[0], e: React.MouseEvent) => {
    e.stopPropagation()
    // Find the user message that prompted this response
    const messageIndex = messages.findIndex((m) => m.id === message.id)
    if (messageIndex > 0) {
      const userMessage = messages[messageIndex - 1]
      if (userMessage.role === "user") {
        // Re-send the user message using same model as original message or default
        const modelToUse = message.model_id || QUICKPATCH_DEFAULT_MODEL
        if (generationMode === "QUICKPATCH" && currentHtml) {
          void sendQuickPatch(userMessage.content, currentHtml, modelToUse)
        } else {
          const pageContext = generationMode === "EDIT" ? currentHtml : undefined
          void sendMessage(userMessage.content, pageContext, modelToUse, generationMode === "EDIT" ? "EDIT" : "CREATE")
        }
        toast.success("Regenerating response...")
      }
    }
  }

  return (
    <div className="w-[420px] border-r border-border bg-background flex flex-col">
      {/* Header with Conversation Threads */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="relative" ref={conversationsDropdownRef}>
            <button
              onClick={() => setShowConversationsDropdown(!showConversationsDropdown)}
              className="flex items-center gap-2 hover:bg-secondary rounded-lg px-2 py-1.5 transition-colors"
            >
              <MessagesSquare className="w-4 h-4 text-primary" />
              <span className="font-semibold text-foreground text-sm truncate max-w-[200px]">
                {currentConversation?.title || "Chat"}
              </span>
              <ChevronDown
                className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${
                  showConversationsDropdown ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Conversations Dropdown */}
            {showConversationsDropdown && (
              <div className="absolute top-full left-0 mt-1 w-72 bg-background border border-border rounded-lg shadow-lg z-50">
                {/* Conversations List */}
                <div className="py-1 max-h-64 overflow-y-auto">
                  {conversations.length > 0 ? (
                    conversations.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => handleConversationSwitch(conv.id)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-secondary transition-colors flex items-center justify-between ${
                          conv.id === conversationId ? "bg-secondary font-medium" : ""
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{conv.title}</div>
                          <div className="text-xs text-muted-foreground">{conv.message_count} messages</div>
                        </div>
                        {conv.is_active && (
                          <span className="text-xs px-1.5 py-0.5 bg-green-500/20 text-green-300 rounded ml-2">
                            Active
                          </span>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-muted-foreground">No conversations yet</div>
                  )}
                </div>

                {/* Divider */}
                <div className="border-t border-border" />

                {/* New Conversation Button */}
                <button
                  onClick={() => {
                    setShowConversationsDropdown(false)
                    onCreateConversation?.()
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-secondary transition-colors"
                >
                  <MessageSquarePlus className="w-4 h-4" />
                  New Thread
                </button>
              </div>
            )}
          </div>

          {(isStreaming || isQuickPatchStreaming) && (
            <span className="flex items-center gap-1 text-xs text-primary">
              <Loader2 className="w-3 h-3 animate-spin" />
              {isQuickPatchStreaming ? `Patching (${patchCount})` : "Streaming"}
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && !isStreaming ? (
          <div className="text-center py-8 text-muted-foreground">
            <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Start a conversation to generate or modify your design.</p>
            <div className="mt-4 space-y-2 text-xs text-left">
              <p className="font-medium">Try prompts like:</p>
              <p className="text-muted-foreground">&quot;Create a modern landing page with a hero section&quot;</p>
              <p className="text-muted-foreground">&quot;Add a pricing table with 3 tiers&quot;</p>
              <p className="text-muted-foreground">&quot;Change the color scheme to dark mode&quot;</p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                onClick={() => handleSelectMessage(message)}
                className={`group cursor-pointer rounded-lg p-3 transition-colors ${
                  message.role === "user" ? "bg-primary/10 hover:bg-primary/20" : "bg-secondary hover:bg-secondary/80"
                }`}
              >
                {/* Message Header */}
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {message.role === "user" ? (
                    <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                      <User className="w-3 h-3 text-primary-foreground" />
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <span className="text-xs text-muted-foreground">{formatTime(message.created_at)}</span>
                  {/* Version badge */}
                  {message.version_number && (
                    <span className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded">
                      v{message.version_number}
                    </span>
                  )}
                  {/* Model badge for assistant messages */}
                  {message.role === "assistant" && message.model_id && (
                    <span className="text-xs px-1.5 py-0.5 bg-purple-500/20 text-purple-300 rounded">
                      {getModelName(message.model_id)}
                    </span>
                  )}
                  {/* Applied indicator */}
                  {message.is_applied && (
                    <span className="text-xs px-1.5 py-0.5 bg-green-500/20 text-green-300 rounded">Current</span>
                  )}
                </div>

                {/* Message Content */}
                <p className="text-sm text-foreground leading-relaxed">{message.content}</p>

                {/* Metrics and Actions Row */}
                <div className="mt-2 flex items-center justify-between">
                  {/* Metrics for assistant messages */}
                  {message.role === "assistant" &&
                  (message.tokens_input || message.tokens_output || message.generation_duration_ms) ? (
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      {message.generation_duration_ms && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDuration(message.generation_duration_ms)}
                        </span>
                      )}
                      {message.tokens_input && <span title="Input tokens">↑{formatTokens(message.tokens_input)}</span>}
                      {message.tokens_output && (
                        <span title="Output tokens">↓{formatTokens(message.tokens_output)}</span>
                      )}
                    </div>
                  ) : (
                    <div />
                  )}

                  {/* Action buttons */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {/* Copy */}
                    <button
                      onClick={(e) => handleCopyMessage(message.content, e)}
                      className="p-1 rounded hover:bg-background text-muted-foreground hover:text-foreground"
                      title="Copy message"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    {/* Regenerate (only for assistant messages) */}
                    {message.role === "assistant" && (
                      <button
                        onClick={(e) => handleRegenerateMessage(message, e)}
                        className="p-1 rounded hover:bg-background text-muted-foreground hover:text-foreground"
                        title="Regenerate response"
                        disabled={isGenerating}
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {/* Delete */}
                    <button
                      onClick={(e) => handleDeleteMessage(message.id, e)}
                      className="p-1 rounded hover:bg-red-500/20 text-muted-foreground hover:text-red-400"
                      title="Delete message"
                      disabled={deleteMessageMutation.isLoading}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Preview Thumbnail and View Button (for messages with HTML) */}
                {message.generated_html && (
                  <div className="mt-3">
                    {/* Mini Preview */}
                    <div className="relative aspect-video bg-secondary rounded border border-border overflow-hidden mb-2">
                      <iframe
                        srcDoc={message.generated_html}
                        className="w-full h-full pointer-events-none"
                        style={{
                          transform: "scale(0.25)",
                          transformOrigin: "top left",
                          width: "400%",
                          height: "400%",
                        }}
                        title="Preview"
                        sandbox="allow-scripts"
                      />
                    </div>
                    {/* View Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleSelectMessage(message)
                      }}
                      className="flex items-center gap-1.5 w-full px-3 py-1.5 text-xs font-medium bg-primary/10 hover:bg-primary/20 text-primary rounded-md transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      {message.version_number ? `View v${message.version_number}` : "View this version"}
                    </button>
                  </div>
                )}
              </div>
            ))}

            {/* Streaming assistant message */}
            {isStreaming && (
              <div className="rounded-lg p-3 bg-secondary">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <Loader2 className="w-3 h-3 text-white animate-spin" />
                  </div>
                  <span className="text-xs text-muted-foreground">Generating...</span>
                </div>

                {/* Streaming explanation */}
                {streamingExplanation && (
                  <p className="text-sm text-foreground leading-relaxed mb-2">
                    {streamingExplanation}
                    <span className="inline-block w-2 h-4 bg-primary/50 animate-pulse ml-0.5" />
                  </p>
                )}

                {/* Streaming HTML indicator - simplified for performance */}
                {streamingHtml && (
                  <div className="mt-2 p-2 bg-background/50 rounded border border-border">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Generating HTML ({streamingHtml.length.toLocaleString()} chars)...</span>
                    </div>
                    {/* Progress bar instead of live iframe - much better performance */}
                    <div className="mt-2 h-1 bg-secondary rounded overflow-hidden">
                      <div
                        className="h-full bg-primary/60 transition-all duration-300"
                        style={{ width: `${Math.min(100, (streamingHtml.length / 5000) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* QuickPatch streaming indicator */}
            {isQuickPatchStreaming && (
              <div className="rounded-lg p-3 bg-green-500/10 border border-green-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-5 h-5 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                    <Loader2 className="w-3 h-3 text-white animate-spin" />
                  </div>
                  <span className="text-xs text-muted-foreground">Applying patches...</span>
                  <span className="text-xs px-1.5 py-0.5 bg-green-500/20 text-green-300 rounded">
                    {patchCount} patches
                  </span>
                </div>

                {/* Streaming patches preview */}
                {streamingPatches && (
                  <div className="mt-2 p-2 bg-background/50 rounded border border-border">
                    <pre className="text-xs text-muted-foreground overflow-hidden max-h-20 font-mono">
                      {streamingPatches.slice(0, 200)}
                      {streamingPatches.length > 200 && "..."}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-3 border-t border-border">
        <AuraPromptInput
          variant="compact"
          value={input}
          onChange={setInput}
          onSubmit={handleSubmit}
          isLoading={isGenerating}
          showModeToggle
          generationMode={generationMode}
          onModeChange={setGenerationMode}
          currentPageName={currentPageName}
          styleGuideSource={styleGuideSource}
          onStyleGuideChange={onStyleGuideChange}
          onExtractStyleGuide={onExtractStyleGuide}
          savedStyleGuides={savedStyleGuides}
          pages={pages}
          onUploadImage={handleUploadImage}
        />
      </div>
    </div>
  )
}

export default AuraChatSidebar
